import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, ChevronLeft, ChevronRight, Eye, Download, Search } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { ExportOptions } from '../components/common/ExportOptions';
import { useExport } from '../hooks/useExport';
import { customerAPI } from '../services/ApiServices';
import { Input } from '../components/ui/Input';

export const Pending = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [exportCustomers, setExportCustomers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [search, setSearch] = useState('');
  const limit = 10;

  const { handleExport } = useExport('pending_customers', 'Pending Customers Report');

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchCustomers();
      fetchAllPendingCustomers();
    }, 300); // debounce

    return () => clearTimeout(delay);
  }, [currentPage, search]);


  const fetchCustomers = async () => {
    setIsLoading(true);
    try {
      const { data, pagination } = await customerAPI.getPendingCustomers({
        page: currentPage,
        limit,
        search
      });
      setCustomers(data);
      setPagination(pagination);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllPendingCustomers = async () => {
    setIsExporting(true);
    try {
      const { data, pagination } = await customerAPI.getPendingCustomers({ search });
      setExportCustomers(data);
      return data;
    } catch (error) {
      console.error('Failed to fetch all customers for export:', error);
      throw error;
    } finally {
      setIsExporting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPaginationRange = (current, total) => {
    const delta = 2; // how many pages around current
    const range = [];
    const rangeWithDots = [];

    for (let i = 1; i <= total; i++) {
      if (
        i === 1 ||
        i === total ||
        (i >= current - delta && i <= current + delta)
      ) {
        range.push(i);
      }
    }

    let last = null;

    for (let i of range) {
      if (last !== null) {
        if (i - last === 2) {
          rangeWithDots.push(last + 1);
        } else if (i - last > 2) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      last = i;
    }

    return rangeWithDots;
  };

  // Define export columns configuration
  const exportColumns = [
    {
      header: 'Sub ID',
      accessor: (item) => `${item.faydaData?.sub || ''}`,
      width: 40,
      pdfWidth: 40,
    },
    {
      header: 'Customer ID',
      accessor: 'ofsData.customerId',
      width: 20,
      pdfWidth: 30
    },
    {
      header: 'Name',
      accessor: 'ofsData.fullName',
      width: 25,
      pdfWidth: 40
    },
    {
      header: 'Email',
      accessor: 'ofsData.email',
      width: 30,
      pdfWidth: 45
    },
    {
      header: 'Phone',
      accessor: (item) => `${item.ofsData?.phone || ''}`,
      width: 15,
      pdfWidth: 25
    },
    {
      header: 'Company Code',
      accessor: 'ofsData.companyCode',
      width: 15,
      pdfWidth: 25
    },
    {
      header: 'Status',
      accessor: 'status',
      width: 12,
      pdfWidth: 20
    },
    {
      header: 'Created At',
      accessor: (item) => formatDate(item.createdAt),
      width: 20,
      pdfWidth: 35
    }
  ];

  const onExport = async (format) => {
    // handleExport(format, customers, exportColumns);
    try {
      // Fetch all pending customers for export
      const allCustomers = await fetchAllPendingCustomers();
      handleExport(format, allCustomers, exportColumns);
    } catch (error) {
      console.error('Export failed:', error);
      // You might want to show an error toast here
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-8 h-8 text-yellow-600" />
              <h1 className="text-3xl font-bold text-gray-900">Pending Verification</h1>
            </div>
            <p className="text-gray-600">Review and verify customer KYC documents</p>
          </div>

          <div className="flex items-center gap-4">
            <div className="bg-yellow-50 px-4 py-2 rounded-lg border border-yellow-200">
              <p className="text-sm font-semibold text-yellow-800">
                {pagination?.total || 0} Pending
              </p>
            </div>

            {isExporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                Exporting...
              </>
            ) : (
              <>
              </>
            )}

            {/* Reusable Export Component */}
            <ExportOptions
              data={exportCustomers}
              onExport={onExport}
              isLoading={isExporting}
              disabled={isLoading}
            />
          </div>
        </div>

        {/* Rest of the component remains the same */}
        <Card padding="none">

          {/* Search */}
          <div className="p-6 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by name, customer ID, or reference number..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
              />
            </div>
          </div>
          {pagination && pagination.total > 0 ? (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Sub ID</TableHeader>
                    <TableHeader>Customer ID</TableHeader>
                    <TableHeader>Name</TableHeader>
                    <TableHeader>Reference No</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Created At</TableHeader>
                    <TableHeader>Action</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customers.map((customer) => (
                    <TableRow key={customer.ofsData.customerId}>
                      <TableCell>
                        <span className="font-mono text-xs font-semibold">
                          {customer.faydaData.sub}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-xs font-semibold">
                          {customer.ofsData.customerId}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{customer.ofsData.fullName}</span>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-xs">{customer.ofsData.customerId}</span>
                      </TableCell>
                      <TableCell>
                        <Badge status={customer.status} />
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600">{formatDate(customer.createdAt)}</span>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          onClick={() => navigate(`/verify/${customer.ofsData.customerId}`, { state: { customer } })}
                          className="gap-2 bg-gradient-to-r from-[#472f92] to-[#e4aa24] text-white"
                        >
                          <Eye className="w-4 h-4" />
                          Verify
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {pagination && pagination.total > 0 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">

                  {/* Info */}
                  <div className="text-sm text-gray-600">
                    Showing {(currentPage - 1) * limit + 1} to{' '}
                    {Math.min(currentPage * limit, pagination.total)} of{' '}
                    {pagination.total} results
                  </div>

                  {/* Controls */}
                  <div className="flex items-center gap-2">
                    {/* Previous */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>

                    {/* Page Numbers */}
                    <div className="flex gap-1">
                      {getPaginationRange(currentPage, pagination.pages).map((page, index) => (
                        <button
                          key={index}
                          onClick={() => page !== '...' && setCurrentPage(page)}
                          disabled={page === '...'}
                          className={`px-3 py-1 rounded-lg text-sm font-medium
      ${page === currentPage
                              ? 'bg-blue-600 text-white'
                              : page === '...'
                                ? 'cursor-default text-gray-400'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>

                    {/* Next */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        setCurrentPage(p => Math.min(pagination.pages, p + 1))
                      }
                      disabled={currentPage === pagination.pages}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

            </>
          ) : (
            <div className="p-12 text-center">
              <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Pending Customers</h3>
              <p className="text-gray-600">All customers have been verified</p>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};